import React from 'react';
import { Grid, Box } from '@mui/material';
import TransformationBase from './common/TransformationBase';
import { TransformationHeading } from './common/fields/Heading';
import { COMMON_STYLES } from './common/Constants';
import { MultiSourceColumns } from './common/fields/MultiSourceColumns';
import { OutputColumnSelector } from './common/fields/OutputColumnSelector';
import { OutputColumnField } from './common/fields/OutputColumnField';
import { CheckBoxField } from './common/fields/CheckBoxField';
import TransformationArrayField from './common/fields/TransformationArrayField';

const INITIALSTATE = {
  type: 'concat_ws',
  alias: '',
  df: '',
  transformations: [
    {
      sourceColumns: ['', ''],
      outputColumn: '',
      selectedOutputSource: '',
      transformInPlace: false,
      param: '', // separator: empty = concat, '-' = concat_ws
    },
  ],
};

const validationSchema = {
  alias: {
    required: true,
    pattern: /^[a-zA-Z0-9_]{1,35}$/,
    message: 'Alias must be 1-35 alphanumeric characters',
  },
  'transformations.selectedOutputSource': {
    validate: (value, transform) => {
      if (transform.transformInPlace && !value?.trim()) {
        return 'At least one output source column must be selected';
      }
      return '';
    },
  },
  'transformations.sourceColumns': {
    validate: (value, transform) => {
      const columnErrors = value.map((col) => (!col ? 'Column is required' : ''));
      if (value.filter(Boolean).length < 2) {
        return columnErrors.map((e, i) => (i < 2 && !value[i] ? 'At least 2 columns required' : e));
      }
      return columnErrors.some(Boolean) ? columnErrors : '';
    },
  },
  'transformations.outputColumn': {
    validate: (value, transform) => {
      if (!transform.transformInPlace && !value?.trim()) {
        return 'Output column is required';
      }
      return '';
    },
  },
};

const renderTransformationFields = ({ formField, errors, setFieldValue, disableForm, headers }) => {
  const renderField = ({ transform, index, headers, disableForm, errors }) => {
    const updateField = (key, value) => {
      const newTransforms = [...formField.transformations];
      newTransforms[index][key] = value;
      setFieldValue('transformations', newTransforms);
    };

    const handleSourceChange = (colIndex, value) => {
      const newCols = [...transform.sourceColumns];
      newCols[colIndex] = value;
      updateField('sourceColumns', newCols);
    };

    const handleAddColumn = () => {
      updateField('sourceColumns', [...transform.sourceColumns, '']);
    };

    const handleRemoveColumn = (colIndex) => {
      updateField(
        'sourceColumns',
        transform.sourceColumns.filter((_, i) => i !== colIndex)
      );
    };

    const handleInPlaceChange = (checked) => {
      const newTransforms = [...formField.transformations];
      newTransforms[index].transformInPlace = checked;
      if (checked) {
        newTransforms[index].outputColumn = '';
      }
      setFieldValue('transformations', newTransforms);
    };

    const handleOutputChange = (value) => {
      const newTransforms = [...formField.transformations];
      newTransforms[index].outputColumn = value;
      setFieldValue('transformations', newTransforms);
    };

    return (
      <>
        <Grid item xs={12} sm={6}>
          <MultiSourceColumns
            sourceColumns={transform.sourceColumns}
            handleColumnChange={handleSourceChange}
            handleAddColumn={handleAddColumn}
            handleRemoveColumn={handleRemoveColumn}
            headers={headers}
            disableForm={disableForm}
            errors={errors?.sourceColumns || []}
            minColumns={2}
          />
        </Grid>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <OutputColumnField
              value={transform.param || ''}
              onChange={(e) => updateField('param', e.target.value)}
              disabled={disableForm}
              error={!!errors?.param}
              label="Separator"
              helperText={errors?.param || 'Leave blank for concat. Use "," or "-" for custom separator.'}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <OutputColumnSelector
              transformInPlace={transform.transformInPlace}
              setTransformInPlace={(value) => handleInPlaceChange(value)}
              selectedOutputSource={transform.selectedOutputSource}
              setSelectedOutputSource={(val) => {
                const newTransforms = [...formField.transformations];
                newTransforms[index].selectedOutputSource = val;
                newTransforms[index].outputColumn = val;
                setFieldValue('transformations', newTransforms);
              }}
              outputColumn={transform.outputColumn}
              setOutputColumn={handleOutputChange}
              sourceColumns={transform.sourceColumns.filter((col) => col)}
              headers={headers}
              disabled={disableForm}
              errors={errors || {}}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Box sx={COMMON_STYLES.container}>
      <TransformationHeading title="Concat with Separator - A,B → AB or A-B" disabled={disableForm} />
      <TransformationArrayField
        transformations={formField.transformations}
        getNewTransform={() => ({
          sourceColumns: ['', ''],
          outputColumn: '',
          selectedOutputSource: '',
          transformInPlace: false,
          param: '',
        })}
        setTransformations={(newTransforms) => setFieldValue('transformations', newTransforms)}
        renderField={renderField}
        errors={errors.transformations || []}
        headers={headers}
        disableForm={disableForm}
      />
    </Box>
  );
};

const NodeModal_ConcatWs = (props) => (
  <TransformationBase
    {...props}
    INITIALSTATE={INITIALSTATE}
    validationSchema={validationSchema}
    renderTransformationFields={renderTransformationFields}
    transformationType={INITIALSTATE.type}
    tranformationName="Concat with Separator"
  />
);

export default NodeModal_ConcatWs;
